import { clsx } from 'clsx';
import React, { useState, useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';

interface ShiftTypeOption {
  key: string;
  label: string;
  icon: React.ReactNode;
}

interface ShiftTypeSelectorProps {
  options: ShiftTypeOption[];
  onChange?: (selectedTypes: string[]) => void;
  className?: string;
  initialValue?: string[];
  disabled?: boolean;
}

export const ShiftTypeSelector: React.FC<ShiftTypeSelectorProps> = ({
  options,
  onChange,
  className = '',
  initialValue = [],
  disabled = false,
}) => {
  const [selectedTypes, setSelectedTypes] = useState<string[]>(initialValue);

  const prevInitialValueRef = useRef<string[]>(initialValue);
  useEffect(() => {
    const hasChanged =
      prevInitialValueRef.current.length !== initialValue.length ||
      prevInitialValueRef.current.some((item, index) => item !== initialValue[index]);

    if (hasChanged) {
      setSelectedTypes(initialValue);
      prevInitialValueRef.current = initialValue;
    }
  }, [initialValue]);

  const handleToggleType = (key: string) => {
    if (disabled) return;

    const updatedTypes = selectedTypes.includes(key)
      ? selectedTypes.filter((type) => type !== key)
      : [...selectedTypes, key];

    setSelectedTypes(updatedTypes);
    onChange?.(updatedTypes);
  };

  // Keyboard accessibility for clickable divs
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>, key: string) => {
    if (disabled) return;

    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleToggleType(key);
    }
  };

  return (
    <div className={className}>
      <div className="tw:flex tw:gap-2 tw:sm:gap-4">
        {options.map((opt) => {
          const selected = selectedTypes.includes(opt.key);
          return (
            <div
              key={opt.key}
              role="button"
              tabIndex={disabled ? -1 : 0}
              aria-pressed={selected}
              className={twMerge(
                clsx(
                  'tw:!bg-white tw:!flex tw:flex-col tw:gap-2 tw:flex-1 tw:items-center tw:border tw:rounded-md tw:px-2 tw:py-2 tw:transition-colors tw:ease-in-out tw:duration-100',
                  disabled &&
                    selected &&
                    'tw:border-brand-green-1 tw:!bg-brand-green-1/10 tw:text-brand-green-1 tw:dark:border-none tw:dark:text-zinc-300 tw:dark:bg-brand-green-1 tw:!cursor-not-allowed',
                  disabled &&
                    !selected &&
                    'tw:border-gray-300 tw:text-gray-400 tw:bg-gray-100 tw:!cursor-not-allowed tw:dark:border-zinc-600 tw:dark:bg-zinc-700 tw:dark:text-zinc-500 tw:opacity-90',
                  !disabled &&
                    selected &&
                    'tw:border-brand-green-1 tw:!bg-brand-green-1/10 tw:text-brand-green-1 tw:dark:border-none tw:dark:text-zinc-300 tw:dark:bg-brand-green-1 tw:cursor-pointer',
                  !disabled &&
                    !selected &&
                    'tw:border-gray-300 tw:text-zinc-700 tw:bg-zinc-50 tw:hover:border-gray-400 tw:dark:border-zinc-300 tw:dark:bg-white/5 tw:dark:text-zinc-300 tw:dark:border-none tw:cursor-pointer'
                )
              )}
              onClick={() => handleToggleType(opt.key)}
              onKeyDown={(e) => handleKeyDown(e, opt.key)}
            >
              {opt.icon}
              <span className="tw:text-sm tw:font-medium tw:text-center">{opt.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ShiftTypeSelector;
